import api from '@/core/api/client';

export interface WhatsAppServer {
  id: string;
  name: string;
  baseUrl: string;
  adminApiKey: string;
  maxCapacity: number;
  currentLoad: number;
  isActive: boolean;
  isHealthy: boolean;
  lastHealthCheck?: string;
  region?: string;
  priority: number;
  notes?: string;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServerStats {
  global: {
    totalServers: number;
    activeServers: number;
    totalCapacity: number;
    currentLoad: number;
    availableSlots: number;
    utilizationRate: number;
  };
  servers: Array<WhatsAppServer & {
    utilizationRate: number;
    availableSlots: number;
    status: 'HEALTHY' | 'CRITICAL' | 'FULL' | 'UNHEALTHY' | 'INACTIVE';
  }>;
  byRegion: Array<{
    region: string;
    totalServers: number;
    activeServers: number;
    totalCapacity: number;
    currentLoad: number;
    availableSlots: number;
  }>;
}

export interface CreateServerData {
  name: string;
  baseUrl: string;
  adminApiKey: string;
  maxCapacity?: number;
  region?: string;
  priority?: number;
  notes?: string;
  isActive?: boolean;
}

export interface UpdateServerData {
  name?: string;
  baseUrl?: string;
  adminApiKey?: string;
  maxCapacity?: number;
  region?: string;
  priority?: number;
  notes?: string;
  isActive?: boolean;
  isHealthy?: boolean;
}

export interface HealthCheckResult {
  serverId: string;
  serverName: string;
  isHealthy: boolean;
  error?: string;
}

export interface HealthCheckAllResponse {
  total: number;
  healthy: number;
  unhealthy: number;
  results: HealthCheckResult[];
  checkedAt: string;
}

export const whatsappServersService = {
  async list(): Promise<WhatsAppServer[]> {
    const response = await api.get('/whatsapp-servers');
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao listar servidores');
  },

  async get(id: string): Promise<WhatsAppServer> {
    const response = await api.get(`/whatsapp-servers/${id}`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao buscar servidor');
  },

  async create(data: CreateServerData): Promise<WhatsAppServer> {
    const response = await api.post('/whatsapp-servers', data);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || response.data.error || 'Erro ao criar servidor');
  },

  async update(id: string, data: UpdateServerData): Promise<WhatsAppServer> {
    const response = await api.put(`/whatsapp-servers/${id}`, data);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || response.data.error || 'Erro ao atualizar servidor');
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/whatsapp-servers/${id}`);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao deletar servidor');
  },

  async getStats(): Promise<ServerStats> {
    const response = await api.get('/whatsapp-servers/stats');
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao obter estatísticas');
  },

  async healthCheck(id: string): Promise<{ server: WhatsAppServer; healthCheck: Record<string, unknown> }> {
    const response = await api.post(`/whatsapp-servers/${id}/health-check`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao executar health check');
  },

  async healthCheckAll(): Promise<HealthCheckAllResponse> {
    const response = await api.post('/whatsapp-servers/health-check/all');
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Erro ao executar health check');
  },
};
